import { createContext } from 'react';

import type { ComponentType, ReactNode } from 'react';

/**
 * Banner Context
 *
 * 레이아웃에서 Banner 컴포넌트를 value로 넣어 두면, 자식에서 useBannerContext()로 꺼내 마운트할 수 있습니다.
 *
 * @description
 * - GeneralLayout: <BannerProvider value={{ Banner: AnyComponent }}>{children}</BannerProvider>
 * - 사용처: const { Banner } = useBannerContext(); return Banner ? <Banner /> : null;
 */
/** useContext(BannerContext ?? DefaultBannerContext) 패턴용으로 export. 호스트에서 제거 시 undefined */
export const BannerContext = createContext<BannerContextValue | null>(null);

export interface BannerContextValue {
  /** 레이아웃에서 주입한 배너 컴포넌트. 사용처에서 꺼내 <Banner /> 로 마운트 */
  Banner?: ComponentType;
}

const defaultContextValue: BannerContextValue = {};

interface BannerProviderProps {
  children: ReactNode;
  value?: BannerContextValue;
}

/**
 * Banner Provider
 *
 * value에 Banner 컴포넌트를 넣으면, 자식에서 useBannerContext()로 꺼내 렌더링할 수 있습니다.
 *
 * @param children - 자식 컴포넌트
 * @param value - { Banner?: ComponentType } 형태. 미전달 시 Banner 없음
 */
export function BannerProvider({ children, value = defaultContextValue }: BannerProviderProps) {
  return <BannerContext.Provider value={value}>{children}</BannerContext.Provider>;
}
